// Data + formatting helpers for the หนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ)
// withholding-tax certificate. Pure functions — no React.

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0 }

const TH_MON = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

// ISO / Date → "01 ก.ค. 2569" (full BE year) or "01 ก.ค. 69" (2-digit) when short.
export function thaiDateBE(input, { short = false } = {}) {
  if (!input) return ''
  const d = input instanceof Date ? input : new Date(String(input).length <= 10 ? String(input) + 'T00:00:00' : input)
  if (Number.isNaN(d.getTime())) return ''
  const be = d.getFullYear() + 543
  const yr = short ? String(be).slice(-2) : String(be)
  return `${String(d.getDate()).padStart(2, '0')} ${TH_MON[d.getMonth()]} ${yr}`
}

// Thai baht text — อ่านจำนวนเงินเป็นตัวอักษร (e.g. 270 → "สองร้อยเจ็ดสิบบาทถ้วน").
export function bahtText(amount) {
  const n = num(amount)
  if (n === 0) return 'ศูนย์บาทถ้วน'
  const neg = n < 0
  const abs = Math.abs(n)
  const baht = Math.floor(abs)
  const satang = Math.round((abs - baht) * 100)
  let out = ''
  if (baht > 0) out += readThaiInt(baht) + 'บาท'
  if (satang > 0) out += readThaiInt(satang) + 'สตางค์'
  else out += baht > 0 ? 'ถ้วน' : ''
  if (baht === 0 && satang > 0) out = readThaiInt(satang) + 'สตางค์'
  return (neg ? 'ลบ' : '') + out
}

function readThaiInt(num0) {
  const digits = ['ศูนย์', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า']
  const places = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน']
  const n = Math.floor(Math.abs(num0))
  if (n === 0) return 'ศูนย์'
  if (n >= 1000000) return readThaiInt(Math.floor(n / 1000000)) + 'ล้าน' + (n % 1000000 ? readThaiInt(n % 1000000) : '')
  let s = ''
  const str = String(n)
  const len = str.length
  for (let i = 0; i < len; i++) {
    const d = Number(str[i])
    const place = len - i - 1
    if (d === 0) continue
    if (place === 0 && d === 1 && len > 1) { s += 'เอ็ด'; continue }
    if (place === 1 && d === 2) { s += 'ยี่' + places[place]; continue }
    if (place === 1 && d === 1) { s += places[place]; continue }
    s += digits[d] + places[place]
  }
  return s
}

export function money2(n) {
  return num(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Resolve the branch's COMPANY object (the payer / ผู้มีหน้าที่หักภาษี), tolerant of
// the several field-name variants the Xano branch record uses.
export function pickCompany(branch) {
  const c = (branch && (branch.company || branch.Company)) || {}
  return {
    name: String(c.name || c.company_name || '').trim(),
    taxid: String(c.tax_number ?? c.taxnumber ?? c.tax_id ?? c.taxid ?? c.tax_no ?? '').trim(),
    address: String(c.address ?? c.addr ?? c.address1 ?? c.full_address ?? c.location ?? '').trim(),
    // authorised signatory (กรรมการผู้มีอำนาจ). Falls back to a constant if the
    // company record doesn't carry one.
    signatory: String(c.director ?? c.signatory ?? c.authorized_person ?? c.authorised_person ?? c.signer ?? '').trim(),
  }
}

// Build every field the certificate needs from an expense + its branch record.
export function buildWhtModel(expense, branch) {
  const a = expense?.amount || {}
  const s = expense?.seller || {}
  const company = pickCompany(branch)

  const base = num(a.total_price)            // จำนวนเงินที่จ่าย (WHT base, pre-VAT)
  const wht = num(a.WHT)                      // ภาษีที่หักและนำส่งไว้
  const payDate = (expense?.date_buy || expense?.date_display || '').slice(0, 10)

  // เลขที่ = ปี-เดือน-idค่าใช้จ่าย  (e.g. 2026-7-5323)
  const yr = num(expense?.expense_year) || (payDate ? new Date(payDate).getFullYear() : '')
  const mo = num(expense?.expense_month_num) || (payDate ? new Date(payDate).getMonth() + 1 : '')
  const docNo = [yr, mo, expense?.id ?? expense?.expense_id].filter((x) => x !== '' && x != null).join('-')

  return {
    docNo,
    payer: company,                          // ผู้มีหน้าที่หักภาษี (branch company)
    payee: {                                 // ผู้ถูกหักภาษี (the seller)
      name: String(s.seller_name || '').trim(),
      taxid: String(s.seller_taxid || '').trim(),
      address: String(s.seller_address || '').trim(),
    },
    form: 'pnd53', // always ภ.ง.ด.53 (matches the existing certificate output)
    incomeType: String(a.WHT_type || expense?.detail || '').trim(),  // row-6 "อื่นๆ ระบุ …"
    payDateBE: thaiDateBE(payDate),
    base,
    wht,
    whtText: bahtText(wht),
    issueDateBE: thaiDateBE(new Date(), { short: true }),
    signatoryName: company.signatory || 'ราม บรรพพงษ์',
  }
}
